import { TRANSPARENT_IMAGE_PLACEHOLDER_SRC } from '@/app/components/media/app-image/shared/image-placeholder';
import { safeCall } from '@/app/utils/safeCall';

export type ImageLoading = 'eager' | 'lazy';
export type ImageFetchPriority = 'high' | 'low' | 'auto';

export interface ImageLoadDecisionInput {
  imageKey: string;
  fade?: boolean | null;
  placeholder?: boolean | null;
  preload?: boolean | null;
  loading?: ImageLoading;
  isInViewport?: boolean;
  loadedImageKey?: string | null;
  shouldAnimateShimmer?: boolean;
}

export interface ImageFetchPriorityInput {
  fetchPriority?: ImageFetchPriority;
}

export const shouldNotifyImageLoad = (src: string): boolean =>
  src.trim() !== TRANSPARENT_IMAGE_PLACEHOLDER_SRC;

export const handleRealImageLoad = <
  TEvent extends { currentTarget: { src: string } },
>(
  event: TEvent,
  onInternalLoad: () => void,
  onLoad?: (event: TEvent) => void
): boolean => {
  if (!shouldNotifyImageLoad(event.currentTarget.src)) return false;

  onInternalLoad();
  safeCall(onLoad, event);
  return true;
};

export const getImageFetchPriority = ({
  fetchPriority,
}: ImageFetchPriorityInput): ImageFetchPriority | undefined => fetchPriority;

export const getImageLoadDecision = ({
  imageKey,
  fade = true,
  placeholder = false,
  preload = false,
  loading,
  isInViewport = false,
  loadedImageKey = null,
  shouldAnimateShimmer = false,
}: ImageLoadDecisionInput) => {
  const shouldPreloadImage = !!preload;
  const shouldLoadImmediately = shouldPreloadImage || loading === 'eager';
  const shouldLoadImage = shouldLoadImmediately || isInViewport;
  const shouldFade = !shouldLoadImmediately && !!fade;
  const shouldShowPlaceholder = !shouldLoadImmediately && !!placeholder;
  const isLoaded = shouldLoadImmediately || loadedImageKey === imageKey;
  const loadingAttr: ImageLoading | undefined = shouldPreloadImage
    ? undefined
    : shouldLoadImage
      ? 'eager'
      : 'lazy';
  const decodingAttr: 'auto' | 'async' = shouldLoadImmediately
    ? 'auto'
    : 'async';

  return {
    shouldPreloadImage,
    shouldLoadImmediately,
    shouldLoadImage,
    shouldFade,
    shouldShowPlaceholder,
    shouldAnimatePlaceholder:
      shouldShowPlaceholder && shouldAnimateShimmer && !isLoaded,
    isLoaded,
    loadingAttr,
    decodingAttr,
  };
};
